import { useState, useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectAccountIsOnboardingCompleted } from 'selectors/app'
import { useBilling } from 'ducks/billing/hooks'
import { doConfirmShopifyActiveSubscription } from 'ducks/billing/operations'
import { doFetchAppBootstrapData } from 'actions/app'
import selectIntegrationCredentialsForProvider from 'ducks/integrations/selectors/selectIntegrationCredentialsForProvider'
import { selectQueryParams } from 'selectors/location'
import { doFinishOnboarding } from '../actions'

export function useTrialPage() {
  const dispatch = useDispatch()
  const { check = false } = useSelector(selectQueryParams)
  const [saving, setIsSaving] = useState(false)
  const [confirming, setIsConfirming] = useState(false)
  const [errorCode, setErrorCode] = useState(null)
  const isOnboardingComplete = useSelector(selectAccountIsOnboardingCompleted)
  const {
    externalApprovePendingSubscription,
    startTrial: createPendingSubscription,
    reloadBilling,
    pendingSubscription,
  } = useBilling()

  const { storeName } =
    useSelector(state =>
      selectIntegrationCredentialsForProvider(state, { provider: 'SHOPIFY_V2' })
    ) || {}

  useEffect(
    () => {
      reloadBilling()
      dispatch(doFetchAppBootstrapData())
    },
    [reloadBilling, dispatch]
  )

  const startTrial = useCallback(
    async () => {
      setIsSaving(true)
      setErrorCode(null)
      try {
        if (!pendingSubscription?.approvalUrl) {
          await createPendingSubscription()
        }
        await externalApprovePendingSubscription({ waitForApproval: true })
      } catch (e) {
        setIsSaving(false)
        setErrorCode(e)
      }
    },
    [
      externalApprovePendingSubscription,
      createPendingSubscription,
      pendingSubscription,
    ]
  )

  const confirmShopifyActiveSubscription = useCallback(
    async () => {
      try {
        setIsConfirming(true)
        setErrorCode(null)
        await dispatch(doConfirmShopifyActiveSubscription())
        dispatch(doFinishOnboarding())
      } catch (e) {
        setIsConfirming(false)
        setErrorCode(e)
      }
    },
    [dispatch]
  )

  useEffect(
    () => {
      if (check) confirmShopifyActiveSubscription()
    },
    [check, confirmShopifyActiveSubscription]
  )

  return {
    isOnboardingComplete,
    saving,
    confirming,
    startTrial,
    storeName,
    errorCode,
  }
}
